import { defineStore } from 'pinia'
import axios from 'axios'
import { ref, computed } from 'vue'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref(localStorage.getItem('token') || '')
  const refreshToken = ref(localStorage.getItem('refreshToken') || '')
  const user = ref(null)
  const isAuthenticated = computed(() => !!accessToken.value)
  const loading = ref(false)
  const error = ref('')

  async function login(email, password) {
    loading.value = true
    error.value = ''
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/login`, { email, password })
      accessToken.value = response.data.token || response.data.idToken || response.data.access_token
      refreshToken.value = response.data.refreshToken || response.data.refresh_token
      localStorage.setItem('token', accessToken.value)
      localStorage.setItem('refreshToken', refreshToken.value)
      await fetchUserData()
      loading.value = false
      return true
    } catch (e) {
      error.value = e.response?.data?.detail || 'Błędny e-mail lub hasło.'
      loading.value = false
      return false
    }
  }

  async function fetchUserData() {
    if (!accessToken.value) return
    
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/me`, {
        headers: {
          Authorization: `Bearer ${accessToken.value}`
        }
      })
      user.value = response.data
    } catch (e) {
      user.value = null
    }
  }

  function logout() {
    accessToken.value = ''
    refreshToken.value = ''
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
  }

  async function refreshTokenIfNeeded() {
    if (!refreshToken.value) {
      logout()
      return false
    }
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/refresh`, {
        refresh_token: refreshToken.value
      })
      accessToken.value = response.data.token || response.data.idToken || response.data.access_token
      refreshToken.value = response.data.refresh_token || response.data.refreshToken
      localStorage.setItem('token', accessToken.value)
      localStorage.setItem('refreshToken', refreshToken.value)
      await fetchUserData()
      return true
    } catch (e) {
      console.error('Błąd odświeżania tokena:', e)
      logout()
      // Emituj event o wylogowaniu - interceptor w main.js obsłuży przekierowanie
      window.dispatchEvent(new CustomEvent('auth-logout', {
        detail: { reason: 'refresh-failed' }
      }))
      return false
    }
  }

  async function loginWithGoogle(googleAccessToken) {
    loading.value = true
    error.value = ''
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/google`, { access_token: googleAccessToken })
      accessToken.value = response.data.token
      refreshToken.value = response.data.refresh_token
      localStorage.setItem('token', accessToken.value)
      localStorage.setItem('refreshToken', refreshToken.value)
      await fetchUserData()
      loading.value = false
      return true
    } catch (e) {
      error.value = e.response?.data?.error || 'Błąd logowania przez Google.'
      loading.value = false
      return false
    }
  }

  async function loginWithFacebook(facebookAccessToken) {
    loading.value = true
    error.value = ''
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/facebook`, { access_token: facebookAccessToken })
      accessToken.value = response.data.token
      refreshToken.value = response.data.refresh_token
      localStorage.setItem('token', accessToken.value)
      localStorage.setItem('refreshToken', refreshToken.value)
      await fetchUserData()
      loading.value = false
      return true
    } catch (e) {
      error.value = e.response?.data?.error || 'Błąd logowania przez Facebook.'
      loading.value = false
      return false
    }
  }

  async function loginWithMicrosoft(microsoftAccessToken) {
    loading.value = true
    error.value = ''
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/microsoft`, { access_token: microsoftAccessToken })
      accessToken.value = response.data.token
      refreshToken.value = response.data.refresh_token
      localStorage.setItem('token', accessToken.value)
      localStorage.setItem('refreshToken', refreshToken.value)
      await fetchUserData()
      loading.value = false
      return true
    } catch (e) {
      error.value = e.response?.data?.error || 'Błąd logowania przez Microsoft.'
      loading.value = false
      return false
    }
  }

  async function loginWithApple(identityToken, authorizationCode) {
    loading.value = true
    error.value = ''
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/apple`, { 
        identity_token: identityToken,
        authorization_code: authorizationCode 
      })
      accessToken.value = response.data.token
      refreshToken.value = response.data.refresh_token
      localStorage.setItem('token', accessToken.value)
      localStorage.setItem('refreshToken', refreshToken.value)
      await fetchUserData()
      loading.value = false
      return true
    } catch (e) {
      error.value = e.response?.data?.error || 'Błąd logowania przez Apple.'
      loading.value = false
      return false
    }
  }

  function showLogoutNotification() {
    // Emituj event, który będzie przechwycony przez App.vue
    window.dispatchEvent(new CustomEvent('show-notification', {
      detail: {
        type: 'warning',
        title: 'Sesja wygasła',
        message: 'Zostałeś automatycznie wylogowany. Zaloguj się ponownie.',
        duration: 5000
      }
    }))
  }

  return {
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    fetchUserData,
    refreshTokenIfNeeded,
    showLogoutNotification,
    loginWithGoogle,
    loginWithFacebook,
    loginWithMicrosoft,
    loginWithApple
  }
}) 